import * as firebase from 'firebase/app';
import 'firebase/firestore';

export type FeedbackStatus = 'New' | 'Open' | 'Pending' | 'Closed';
export type FeedbackType = 'Feedback' | 'Question';

/** Normalizes legacy/unknown feedback types to valid FeedbackType values */
export function normalizeFeedbackType(type: string | undefined | null): FeedbackType {
  const map: Record<string, FeedbackType> = {
    'Complaint': 'Feedback',
    'Compliment': 'Feedback',
    'Other': 'Feedback',
    'Query': 'Question',
    'Feedback': 'Feedback',
    'Question': 'Question',
  };
  return map[type || ''] || 'Feedback';
}
export type FeedbackSubject = 
  | 'NewFeedback' 
  | 'DailyPlan' 
  | 'DailyCare' 
  | 'Summary' 
  | 'SummaryNutritionEatingAndDrinking' 
  | 'SummaryActivities' 
  | 'SummaryPersonalCare' 
  | 'SummaryMobility' 
  | 'SummaryCommunication' 
  | 'CarePlan';

export type FeedbackResolution = 'Ongoing' | 'WaitingOnFamily' | 'Resolved';

export interface ChatMedia {
  url: string;
  type: string;
  fileName?: string;
}

// Care Note Models (based on FamilyHub implementation)
export interface CareNote {
  careNoteID: string;
  actionIconID: number;
  actionIconName: string;
  careNoteTextWithSliders: string;
  careNoteText: string;
  duration: number;
  remedialText: string | null;
  happiness: string;
  levelOfHelp: string;
  serviceUser: string;
  serviceUserForeNames: string;
  serviceUserLastName: string;
  serviceUserID: string;
  dateCompletedUTC: string;
  serviceUserTimeZone: string;
}

export interface CarePlanDailyTask {
  actionIconID: number;
  title: string;
  periodName: string;
  preferences: string;
  timeToBeDone: string;
  duration: string;
}

export interface CareSummaryCategories {
  name: string;
  count: number;
  percentage: number;
  color: string;
}

export type CareNoteData = CareNote | CarePlanDailyTask | CareSummaryCategories;

export interface FeedbackMessages {
  id?: string;
  channel?: string;
  facilityId?: string;
  orgId?: string;
  familyId?: string;
  familyPhotoUrl: string;
  familyName: string;
  receiverId?: string;
  readDateTime: firebase.firestore.Timestamp | Date | firebase.firestore.FieldValue;
  read: boolean;
  createdAt: firebase.firestore.Timestamp | Date | firebase.firestore.FieldValue;
  text: string;
  medias: ChatMedia[];
}

export interface FamilyFeedback {
  id?: string;
  orgId: string;
  facilityId: string;
  residentId: string;
  familyMemberId: string;
  familyMemberName: string;
  referenceId: string;
  subject: string;
  details: string;
  careNote?: CareNoteData | null;
  type: FeedbackType;
  status: FeedbackStatus;
  resolution: FeedbackResolution;
  resolutionNote: string;
  closedBy?: string | null;
  channel: string;
  createdAt: Date | firebase.firestore.Timestamp;
  dateClosed?: Date | null | firebase.firestore.Timestamp;
  updatedAt?: Date | null | firebase.firestore.Timestamp;
  updatedById?: string;
  updatedByType?: string;
  unreadMessagesFromStaff: number;
  unreadMessagesFromFamilyMember: number;
  media?: ChatMedia[];
  
  // Runtime fields (fetched from residents collection, not stored in familyFeedback)
  residentName?: string;
  residentRoom?: string;
  familyMemberRelation?: string;
  familyMemberPhotoUrl?: string;
  messages?: FeedbackMessages[];
}

export interface FamilyFeedbackRequest extends Partial<FamilyFeedback> {
  reference?: string;
  feedbackType?: string;
  dateSubmitted?: Date | firebase.firestore.Timestamp;
  staffAssignedId?: string;
  staffAssignedName?: string;
  resolutionComment?: string;
  comments?: FeedbackComment[];
}

export interface FeedbackComment {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderType: 'staff' | 'family';
  timestamp: Date | firebase.firestore.Timestamp;
  attachments?: FeedbackAttachment[];
}

export interface FeedbackAttachment {
  type: 'image';
  url: string;
  thumbnailUrl?: string;
  fileName: string;
}

export interface FeedbackMessage {
  id?: string;
  feedbackId: string;
  feedbackName: string;
  facilityId: string;
  orgId: string;
  residentId: string;
  senderId: string;
  senderName: string;
  senderPhotoUrl: string;
  senderRole: 'staff' | 'family';
  receiverId: string | null;
  text: string;
  medias: FeedbackAttachment[];
  read: boolean;
  readDateTime: Date | firebase.firestore.Timestamp | null;
  createdAt: Date | firebase.firestore.Timestamp;
  createdAtDisplay?: string;
}

export interface FeedbackFamilyHubUser {
  id: string;
  relationship: string;
}

export interface FeedbackRequest {
  id: string;
  residentId: string;
  residentName: string;
  residentRoom: string;
  familyMemberId: string;
  familyMemberName: string;
  familyMemberRelation: string;
  status: 'New' | 'Open' | 'Pending' | 'Closed';
  referenceId: string;
  type: string;
  subject: string;
  details: string;
  createdAt: Date | string;
  updatedAt?: Date | string;
  commentCount: number;
  staffAssignedId?: string;
  staffAssignedName?: string;
  careNote?: string | null;
  channel?: string;
  resolution?: string;
  lastStaffReplyAt?: Date | string;
  lastFamilyReplyAt?: Date | string;
  timeSinceLastUpdate?: string;
  timeSinceLastUpdateColor?: string;
  timeSinceLastUpdateHours?: number; // Numeric value in hours for proper sorting
}

